import axios from 'axios';
import {
  isBackendUnreachableError,
  notifyBackendFailure,
  notifyBackendSuccess,
} from '../utils/backendStatus';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors and backend outages
api.interceptors.response.use(
  (response) => {
    if (!response.config?.skipBackendStatus) {
      notifyBackendSuccess();
    }
    return response;
  },
  (error) => {
    // Log detailed error information for debugging
    if (error.response) {
      console.error('API Error Response:', {
        status: error.response.status,
        statusText: error.response.statusText,
        url: error.config?.url,
        method: error.config?.method,
        data: error.response.data,
        headers: error.response.headers
      });
    } else if (error.request) {
      console.error('API Request Error (no response):', {
        url: error.config?.url,
        method: error.config?.method,
        message: error.message
      });
    } else {
      console.error('API Error:', error.message);
    }

    if (error.response?.status === 401) {
      // Only redirect if we're not already on the login or register page
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/register') {
        console.error('❌ 401 Unauthorized - Token invalid or expired. Logging out...');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      // For login/register pages, let the error be handled by the component
    } else if (error.response?.status === 403) {
      // 403 means authenticated but not authorized - don't logout, just log error
      console.error('❌ 403 Forbidden - You do not have permission to access this resource');
      const detail = error.response?.data?.detail;
      if (detail) {
        if (typeof detail === 'string') {
          console.error('Detail:', detail);
        } else if (Array.isArray(detail)) {
          console.error('Detail:', detail.map(d => typeof d === 'string' ? d : JSON.stringify(d)).join(', '));
        } else {
          console.error('Detail:', JSON.stringify(detail));
        }
      }
    } else if (error.response?.status === 503) {
      // Service unavailable
      console.error('❌ 503 Service Unavailable - Backend service may be down');
    } else if (!error.response) {
      // Network error
      console.error('❌ Network Error - Check if backend services are running');
    }

    if (!error.config?.skipBackendStatus) {
      if (isBackendUnreachableError(error)) {
        notifyBackendFailure();
      } else if (error.response) {
        notifyBackendSuccess();
      }
    }

    return Promise.reject(error);
  }
);

export const systemAPI = {
  healthCheck: () =>
    api.get('/health', {
      timeout: 8000,
      skipBackendStatus: true,
    }),
};

export const authAPI = {
  login: (email, password) =>
    api.post('/auth/login', { email, password }),
  
  register: (userData) =>
    api.post('/auth/register', userData),
  
  getProfile: () =>
    api.get('/auth/profile'),
  
  verifyEmailCode: (email, code) =>
    api.post('/auth/verify-email-code', { email, code }),
  
  resendVerificationCode: (email) =>
    api.post('/auth/resend-verification-code', null, { 
      params: { email },
      headers: { 'Content-Type': 'application/json' }
    }),
  
  changePassword: (passwordData) =>
    api.post('/auth/change-password', passwordData),
  
  resetPassword: (resetData) =>
    api.post('/auth/reset-password', resetData),
};

export const dashboardAPI = {
  getDashboard: (userType) =>
    api.get(`/dashboard/${userType}`),
  
  getStats: (userType) =>
    api.get(`/dashboard/${userType}/stats`),
};

export const candidateAPI = {
  getProfile: () =>
    api.get('/candidates/me'),
  
  updateProfile: (profileData) =>
    api.put('/candidates/me', profileData),
  
  uploadResume: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/candidates/me/upload-resume', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Presigned URL for viewing/downloading own resume (disposition: 'inline' | 'attachment')
  getResumeAccessUrl: (disposition = 'inline') =>
    api.get('/candidates/me/resume/url', { params: { disposition } }),

  updatePreferences: (payload) =>
    api.patch('/candidates/me/preferences', payload),

  getNotifications: () =>
    api.get('/candidates/me/notifications'),
  
  // Prescreening methods
  generatePrescreeningQuestions: (maxQuestions = 10) =>
    api.post('/candidates/me/prescreening/generate', null, { params: { max_questions: maxQuestions } }),
  
  getPrescreeningQuestions: () =>
    api.get('/candidates/me/prescreening/questions'),
  
  submitPrescreeningAnswers: (answers) =>
    api.post('/candidates/me/prescreening/answers', answers),
  
  completePrescreeningSession: () =>
    api.post('/candidates/me/prescreening/complete'),
  
  getPrescreeningSession: () =>
    api.get('/candidates/me/prescreening/session'),
  
  getPrescreeningSessions: () =>
    api.get('/candidates/me/prescreening/sessions'),
  
  // Job recommendations and applications
  getJobRecommendations: (page = 1, pageSize = 10, skills = null) => {
    const params = { page, page_size: pageSize };
    if (skills && skills.length > 0) {
      params.skills = skills;
    }
    return api.get('/jobs/recommendations', { params });
  },
  
  applyToJob: (jobId, applicationData) =>
    api.post(`/jobs/${jobId}/apply`, applicationData),
  
  getJobDetails: (jobId) =>
    api.get(`/jobs/${jobId}`),
  
  getMyApplications: () =>
    api.get('/applications'),
};

export const employerAPI = {
  getProfile: () =>
    api.get('/employers/me'),
  
  updateProfile: (profileData) =>
    api.put('/employers/me', profileData),
  
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/employers/me/upload-logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  uploadCompanyLogo: (formData) =>
    api.post('/employers/me/upload-logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  getEmployerById: (employerId) =>
    api.get(`/employers/${employerId}`),

  getEmployees: () =>
    api.get('/employers/me/employees'),

  createEmployee: (data) =>
    api.post('/employers/me/employees', data),

  updateEmployee: (employeeId, data) =>
    api.patch(`/employers/me/employees/${employeeId}`, data),

  deleteEmployee: (employeeId) =>
    api.delete(`/employers/me/employees/${employeeId}`),

  // Job Postings (using job-service)
  createJobPosting: (formData) =>
    api.post('/jobs/create', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  getJobPostings: () =>
    api.get('/jobs/my'),

  getJobPosting: (jobPostingId) =>
    api.get(`/jobs/${jobPostingId}`),

  updateJobPosting: (jobPostingId, formData) =>
    api.put(`/jobs/${jobPostingId}/update`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  deleteJobPosting: (jobPostingId) =>
    api.delete(`/jobs/${jobPostingId}`),

  // Job Applications
  getApplications: (page = 1, pageSize = 10, jobId = null) => {
    const params = { page, page_size: pageSize };
    if (jobId) {
      params.job_id = jobId;
    }
    return api.get('/jobs/applications', { params });
  },

  getJobApplications: (jobId, page = 1, pageSize = 10) => {
    return api.get(`/jobs/${jobId}/applications`, {
      params: { page, page_size: pageSize }
    });
  },

  // Candidate Detail Requests
  getCandidateDetailUsage: () =>
    api.get('/employers/me/candidate-detail-requests/usage'),
  
  createCandidateDetailRequests: (data) => {
    // Support both old format (array) and new format (object with candidateIds and jobId)
    if (Array.isArray(data)) {
      return api.post('/employers/me/candidate-detail-requests', { candidateIds: data });
    }
    return api.post('/employers/me/candidate-detail-requests', data);
  },
  
  getCandidateDetailRequests: (statusFilter = null) => {
    const params = statusFilter ? { status: statusFilter } : {};
    return api.get('/employers/me/candidate-detail-requests', { params });
  },
  
  getCandidateRequestStatuses: (candidateIds) => {
    const candidateIdsStr = Array.isArray(candidateIds) ? candidateIds.join(',') : candidateIds;
    return api.get('/employers/me/candidate-detail-requests/statuses', { 
      params: { candidate_ids: candidateIdsStr } 
    });
  },

  // Get all candidates (for employers - minimal details)
  getAllCandidates: (page = 1, pageSize = 50, search = '') =>
    api.get('/employers/candidates', { params: { page, page_size: pageSize, search } }),
  
  // Get matched candidates for a specific job
  getMatchedCandidates: (jobId, page = 1, pageSize = 10, minMatchScore = 0) =>
    api.get(`/jobs/${jobId}/matched-candidates`, { 
      params: { page, page_size: pageSize, min_match_score: minMatchScore } 
    }),
  
  // Get detailed candidate information (for employer view)
  getCandidateDetails: (candidateId) =>
    api.get(`/candidates/${candidateId}/details`),
  
  // Get all skills (public/employer accessible)
  getAllSkills: () =>
    api.get('/skills'),
};

export const adminAPI = {
  getCandidates: (page = 1, pageSize = 50, search = '') =>
    api.get('/admin/candidates', { params: { page, page_size: pageSize, search } }),

  resolveCandidateDisplayNames: (userIds) =>
    api.post('/admin/users/candidate-display-names', { user_ids: userIds }),

  getCandidateByUserId: (userId) =>
    api.get(`/admin/candidates/by-user-id/${encodeURIComponent(userId)}`),

  downloadCandidateResumeByUserId: (userId) =>
    api.get(`/admin/candidates/by-user-id/${encodeURIComponent(userId)}/resume`, {
      responseType: 'blob',
    }),

  getEmployers: (page = 1, pageSize = 50, search = '') =>
    api.get('/admin/employers', { params: { page, page_size: pageSize, search } }),
  
  getEmployees: (page = 1, pageSize = 50, search = '') =>
    api.get('/admin/employees', { params: { page, page_size: pageSize, search } }),
  
  getUserStats: () =>
    api.get('/admin/users/stats'),
  
  toggleUserActive: (userId) =>
    api.patch(`/admin/users/${userId}/toggle-active`),
  
  deleteUser: (userId) =>
    api.delete(`/admin/users/${userId}`),
  
  getEmployerRequests: (page = 1, pageSize = 50, statusFilter = 'all', search = '') =>
    api.get('/admin/employer-requests', { params: { page, page_size: pageSize, status_filter: statusFilter, search } }),
  
  getEmployerRequestStats: () =>
    api.get('/admin/employer-requests/stats'),
  
  updateEmployerRequestStatus: (requestId, status, adminNotes = '', companyNotes = '') =>
    api.patch(`/admin/employer-requests/${requestId}`, { status, adminNotes, companyNotes }),
  
  markEmployerRequestAsPending: (requestId, adminNotes = '', companyNotes = '') =>
    api.post(`/admin/employer-requests/${requestId}/mark-as-pending`, { adminNotes, companyNotes }),

  approveEmployerRequest: (requestId, adminNotes = '', companyNotes = '') =>
    api.post(`/admin/employer-requests/${requestId}/approve`, { adminNotes, companyNotes }),
  
  // Get all approved employees across all employers
  getAllApprovedEmployees: (page = 1, pageSize = 50) =>
    api.get('/admin/employees/approved', { params: { page, page_size: pageSize } }),
  
  // Get AI subscription details
  getAISubscriptionDetails: () =>
    api.get('/admin/subscription/ai'),
  
  // Get candidate profile by ID (uses public endpoint, admin can access)
  getCandidateProfile: (candidateId) =>
    api.get(`/candidates/${candidateId}`),
  
  // Update candidate skills (for admin)
  updateCandidateSkills: (candidateId, skills) =>
    api.patch(`/admin/candidates/${candidateId}/skills`, { skills }),
  
  // Update candidate profile (for admin) - fallback method
  updateCandidateProfile: (candidateId, profileData) =>
    api.put(`/admin/candidates/${candidateId}`, profileData),
  
  // Update employer subscription plan
  updateEmployerSubscriptionPlan: (employerId, data) =>
    api.patch(`/admin/employers/${employerId}/subscription-plan`, data),
  
  // Update subscription plan limit (system-wide)
  updateSubscriptionPlanLimit: (planType, limit) =>
    api.patch(`/admin/subscription-plans/${planType}/limit`, { limit }),
  
  // Get subscription plan limits
  getSubscriptionPlanLimits: () =>
    api.get('/admin/subscription-plans/limits'),
  
  // Candidate Detail Requests (Admin)
  getCandidateDetailRequests: (params = {}) =>
    api.get('/admin/candidate-detail-requests', { params }),
  
  approveCandidateDetailRequest: (requestId, adminNotes = '') =>
    api.patch(`/admin/candidate-detail-requests/${requestId}`, { status: 'approved', admin_notes: adminNotes }),
  
  rejectCandidateDetailRequest: (requestId, adminNotes = '') =>
    api.patch(`/admin/candidate-detail-requests/${requestId}`, { status: 'rejected', admin_notes: adminNotes }),
};

export const employerRequestAPI = {
  create: (requestData) =>
    api.post('/employer-requests', requestData),
  
  submitForm: (requestId, additionalInfo) =>
    api.post(`/employer-requests/${requestId}/submit-form`, { requestId, additionalInfo }),
};

export const questionAPI = {
  // Get available assessments for candidate based on their skills
  getRecommendedAssessments: (candidateId) =>
    api.get(`/assessments/recommended/${candidateId}`),
  
  // Get assessments grouped by difficulty level with progression logic
  getAssessmentsByDifficulty: (candidateId) =>
    api.get(`/assessments/by-difficulty/${candidateId}`),
  
  // Note: generateAssessment removed - use recommended assessments instead
  
  // Prescreening methods
  getPrescreeningSessions: (candidateId = null) =>
    candidateId 
      ? api.get(`/admin/candidates/${candidateId}/prescreening/sessions`)
      : api.get('/candidates/me/prescreening/sessions'),
  
  // Get assessment details
  getAssessment: (assessmentId) =>
    api.get(`/assessments/${assessmentId}`),
  
  // Start assessment
  startAssessment: (assessmentId) =>
    api.post(`/assessments/${assessmentId}/start`),

  saveAssessmentProgress: (assessmentId, answers) =>
    api.patch(`/assessments/${assessmentId}/progress`, { answers }),

  getInProgressAssessments: () =>
    api.get('/assessments/me/in-progress'),
  
  // Submit assessment answers
  submitAssessment: (assessmentId, answers) =>
    api.post(`/assessments/${assessmentId}/submit`, answers),
  
  // Get assessment results
  getAssessmentResults: (assessmentId) =>
    api.get(`/assessments/${assessmentId}/results`),

  // Get detailed assessment attempt results
  getAssessmentAttemptDetails: (attemptId) =>
    api.get(`/assessments/attempts/${attemptId}/details`),
  
  // Get candidate's assessment history
  getAssessmentHistory: (candidateId) =>
    api.get(`/assessments/candidate/${candidateId}/history`),
};

// Admin Assessment Management API
export const adminAssessmentAPI = {
  // Get skills overview with question counts (long timeout: can queue behind slow admin operations)
  getSkillsOverview: () =>
    api.get('/admin/assessments/skills-overview', { timeout: 180000 }),
  
  // Get all assessments
  getAllAssessments: () =>
    api.get('/admin/assessments', { timeout: 180000 }),
  
  // Get all skill categories
  getSkillCategories: () =>
    api.get('/admin/assessments/categories'),
  
  // Get skills by category
  getSkillsByCategory: (categoryName) =>
    api.get(`/admin/assessments/categories/${encodeURIComponent(categoryName)}/skills`),
  
  // Generate questions for a skill (OpenAI can take several minutes; gateway allows long upstream wait)
  generateQuestions: (data) =>
    api.post('/admin/assessments/generate-questions', data, { timeout: 620000 }),
  
  // Create new assessment
  createAssessment: (data) =>
    api.post('/admin/assessments/create', data),
  
  // Add new skill
  addSkill: (data) =>
    api.post('/admin/assessments/add-skill', data),

  // Process skill in background (generate questions and assessments)
  processSkillBackground: (skillId, data) =>
    api.post(`/admin/assessments/process-skill/${skillId}`, data),
  
  // Get skill processing status
  getSkillStatus: (skillId) =>
    api.get(`/admin/assessments/skill-status/${skillId}`, { timeout: 120000 }),
  
  // Toggle skill enable/disable status
  toggleSkillActive: (skillId) =>
    api.patch(`/admin/assessments/skills/${skillId}/toggle-active`),
};

// Course Service (via API gateway)
const COURSE_READ_TIMEOUT_MS = 180000;

export const courseAPI = {
  // Must stay below/aligned with gateway GATEWAY_COURSE_GENERATE_TIMEOUT; long enough for slow OpenAI + large courses.
  generateCourse: (data) =>
    api.post('/admin/courses/generate', data, { timeout: 120000 }),
  listCourses: (config = {}) =>
    api.get('/admin/courses', { timeout: COURSE_READ_TIMEOUT_MS, ...config }),
  getCourse: (courseId, config = {}) =>
    api.get(`/admin/courses/${courseId}`, { timeout: COURSE_READ_TIMEOUT_MS, ...config }),
  pendingEnrollments: (config = {}) =>
    api.get('/admin/course-enrollments/pending', { timeout: COURSE_READ_TIMEOUT_MS, ...config }),
  courseProgressOverview: (config = {}) =>
    api.get('/admin/course-enrollments/progress-overview', { timeout: COURSE_READ_TIMEOUT_MS, ...config }),
  downloadAdminCourseSubmission: (enrollmentId, config = {}) =>
    api.get(`/admin/course-enrollments/${enrollmentId}/submission/download`, {
      responseType: 'blob',
      timeout: COURSE_READ_TIMEOUT_MS,
      ...config,
    }),
  approveEnrollment: (enrollmentId, body = {}) =>
    api.patch(`/admin/course-enrollments/${enrollmentId}/approve`, body, { timeout: COURSE_READ_TIMEOUT_MS }),
  rejectEnrollment: (enrollmentId, body = {}) =>
    api.patch(`/admin/course-enrollments/${enrollmentId}/reject`, body, { timeout: COURSE_READ_TIMEOUT_MS }),
  matchingCourses: (skillNames) => {
    const p = new URLSearchParams();
    (skillNames || []).forEach((s) => {
      if (s && String(s).trim()) p.append('skills', String(s).trim());
    });
    const q = p.toString();
    return api.get(q ? `/courses/matching?${q}` : '/courses/matching');
  },
  requestCourseAccess: (courseId) =>
    api.post(`/courses/${courseId}/request-access`),
  myEnrollments: () => api.get('/candidates/me/course-enrollments'),
  getCourseContent: (courseId) => api.get(`/courses/${courseId}/content`),
  getCourseEnrollmentProgress: (courseId, config = {}) =>
    api.get(`/courses/${courseId}/enrollment/progress`, { timeout: COURSE_READ_TIMEOUT_MS, ...config }),
  markCourseDayComplete: (courseId, dayNumber, config = {}) =>
    api.post(
      `/courses/${courseId}/enrollment/days/${dayNumber}/complete`,
      {},
      { timeout: COURSE_READ_TIMEOUT_MS, ...config }
    ),
  uploadCourseSubmission: (courseId, file, config = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/courses/${courseId}/enrollment/submission`, fd, {
      timeout: Math.max(COURSE_READ_TIMEOUT_MS, 120000),
      ...config,
    });
  },
  downloadCourseSubmission: (courseId, config = {}) =>
    api.get(`/courses/${courseId}/enrollment/submission/download`, {
      responseType: 'blob',
      timeout: COURSE_READ_TIMEOUT_MS,
      ...config,
    }),
};

// Reporting Service API - uses separate base URL
const reportingApiClient = axios.create({
  baseURL: process.env.REACT_APP_REPORTING_API_URL || 'http://localhost:8008',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor for reporting API
reportingApiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const reportingAPI = {
  // Generate reports
  generateAssessmentReport: (candidateId, data) =>
    reportingApiClient.post(`/reports/generate/assessment/${candidateId}`, data),
  
  generatePrescreeningReport: (candidateId, data) =>
    reportingApiClient.post(`/reports/generate/prescreening/${candidateId}`, data),
  
  generateImpressionReport: (candidateId, data) =>
    reportingApiClient.post(`/reports/generate/impression/${candidateId}`, data),
  
  // Get reports
  getReport: (reportId) =>
    reportingApiClient.get(`/reports/${reportId}`),
  
  getCandidateReports: (candidateId, reportType = null, limit = 10) => {
    const params = { limit };
    if (reportType) {
      params.report_type = reportType;
    }
    return reportingApiClient.get(`/reports/candidate/${candidateId}`, { params });
  },
  
  // Delete reports
  deleteReport: (reportId) =>
    reportingApiClient.delete(`/reports/${reportId}`),
  
  // Regenerate reports
  regenerateReport: (reportId) =>
    reportingApiClient.post(`/reports/${reportId}/regenerate`),
  
  // Download report as PDF
  downloadReportPDF: (reportId) =>
    reportingApiClient.get(`/reports/${reportId}/download`, {
      responseType: 'blob'
    }),
  
  // Toggle report visibility (active/inactive for candidate)
  toggleReportVisibility: (reportId, isActive) =>
    reportingApiClient.patch(`/reports/${reportId}/visibility`, { candidate_can_view: isActive }),
  
  // Health check
  healthCheck: () =>
    reportingApiClient.get('/health'),
};

/**
 * Convert company logo URL to use backend proxy (avoids CORS issues)
 * @param {string} rawLogo - Original logo URL (S3 URL or local path)
 * @param {string} apiBaseUrl - Base URL for the API
 * @returns {string|null} - Proxied URL or original URL if not S3
 */
export const getProxiedLogoUrl = (rawLogo, apiBaseUrl) => {
  if (!rawLogo) {
    return null;
  }
  
  // If it's an S3 URL, proxy it through the backend to avoid CORS issues
  if (rawLogo.startsWith('http://') || rawLogo.startsWith('https://')) {
    // Check if it's an S3 URL (contains s3.amazonaws.com or s3.region.amazonaws.com)
    if (rawLogo.includes('s3.amazonaws.com') || (rawLogo.includes('s3.') && rawLogo.includes('.amazonaws.com'))) {
      // Extract the S3 key (path after bucket name)
      // URL format: https://bucket-name.s3.region.amazonaws.com/company-logos/filename.png
      // or: https://s3.region.amazonaws.com/bucket-name/company-logos/filename.png
      let s3Key = '';
      if (rawLogo.includes('/company-logos/')) {
        // Extract everything after company-logos/
        s3Key = rawLogo.split('/company-logos/')[1];
        // Remove any query parameters
        s3Key = s3Key.split('?')[0];
        // Construct proxy URL
        return `${apiBaseUrl}/company-logos/company-logos/${s3Key}`;
      } else if (rawLogo.includes('company-logos')) {
        // Try to extract from different URL formats
        const parts = rawLogo.split('company-logos');
        if (parts.length > 1) {
          s3Key = 'company-logos' + parts[1].split('?')[0];
          return `${apiBaseUrl}/company-logos/${s3Key}`;
        }
      }
      // If we can't parse it, return original (might be a CDN URL)
      return rawLogo;
    }
    // Not an S3 URL, return as-is (could be a CDN or other external URL)
    return rawLogo;
  }
  // Local path, prepend API base URL
  return `${apiBaseUrl}${rawLogo}`;
};

export default api;
